/**
 * Segment playback data / URL layer for
 * {@link components/admin/VideoTranscriptViewer} (audit item 30 / A1).
 *
 * Owns: the processed/processing segment partitioning, the response-scoped URL
 * builders (player `<source>`, thumbnail), the authenticated segment download,
 * and the access token. The component keeps the playback-control + transcript
 * sync (video element refs, time math, auto-scroll).
 *
 * Segments are read from the `segments` argument, which the parent sources
 * from the legacy `interview_data.video_segments` JSONB column. That read is
 * deliberately unchanged during the dual-write window (segments are also
 * written to the new `video_segments` table, but the frontend keeps reading
 * the JSONB mirror for now).
 *
 * @module components/hooks/useSegmentPlayback
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import type { VideoSegment } from '../../types';

/**
 * Re-mint the signed playback tokens this often (ms). Comfortably shorter than
 * the server-side token TTL (300s) so an in-progress player never references an
 * about-to-expire token.
 */
const TOKEN_REFRESH_MS = 4 * 60 * 1000;

/**
 * Pick the right file extension for a downloaded segment. Camera/screen
 * segments are MP4 (Cloudflare Stream transcodes them). Audio segments are
 * served directly from R2 as WebM/Opus or MP4/AAC, so the extension must match
 * the actual mime type or the downloaded file won't play.
 */
function extensionForSegment(segment: VideoSegment): string {
  if (segment.segment_kind === 'audio') {
    if (segment.mime_type?.includes('mp4')) return 'm4a';
    return 'webm';
  }
  return 'mp4';
}

/**
 * Build the download filename for a segment. Exported so the filename logic
 * can be unit-tested without driving the full download click flow through
 * jsdom.
 */
export function buildSegmentDownloadFilename(
  sessionId: string,
  segment: VideoSegment,
): string {
  const prefix =
    segment.segment_kind === 'screen'
      ? 'screen-'
      : segment.segment_kind === 'audio'
        ? 'audio-'
        : '';
  const ext = extensionForSegment(segment);
  return `interview-${sessionId.slice(0, 8)}-${prefix}segment-${segment.segment_index}.${ext}`;
}

/** Inputs for {@link useSegmentPlayback}. */
export interface UseSegmentPlaybackArgs {
  /** Interview session id (campaign). */
  sessionId: string;
  /** Response (`interview_data.id`) scope, or undefined for the latest. */
  responseId?: string;
  /** Segments from the legacy JSONB column (read unchanged). */
  segments?: VideoSegment[] | null;
}

/**
 * Terminal segment statuses: `processed` is terminal-success, `failed` is
 * terminal-failure. Every other status (including intermediate ones the DB
 * writes such as `uploading_to_stream` / `stream_ready` / `enabling_download`)
 * is in-flight. The in-flight predicate is expressed as "NOT terminal" so a new
 * or stuck intermediate status can never fall into a no-bucket void (and so
 * `failed` is never miscounted as in-flight and spun on forever).
 */
const TERMINAL_STATUSES = new Set<string>(['processed', 'failed']);

/** Return contract of {@link useSegmentPlayback}. */
export interface UseSegmentPlaybackResult {
  /** Processed segments, sorted by start_ms ascending. */
  processedSegments: VideoSegment[];
  /** Segments still in-flight (any non-terminal status). */
  processingSegments: VideoSegment[];
  /** Terminally-failed segments (status === 'failed'). */
  failedSegments: VideoSegment[];
  /** Whether any processed segment exists. */
  hasProcessedVideo: boolean;
  /** Whether any segment failed terminally. */
  hasFailedSegments: boolean;
  /** True when failures exist and no segment is processed or in-flight. */
  allFailed: boolean;
  /**
   * Build the player `<source>` URL for a segment. Uses a short-lived signed
   * playback token (not the Supabase JWT) in the query string, response scoped.
   */
  segmentSrc: (segment: VideoSegment) => string;
  /**
   * Build the thumbnail URL for a segment. Uses a short-lived signed playback
   * token (not the Supabase JWT) in the query string, response scoped.
   */
  thumbnailSrc: (segment: VideoSegment) => string;
  /**
   * Whether the signed playback token for a segment has been minted yet. Tokens
   * mint asynchronously, so this flips false -> true once. The player keys its
   * `<video>`/`<audio>` on it so the element REMOUNTS when the token lands: a
   * `<video>` does not reload when only its `<source src>` changes, so without a
   * remount it would stay stuck on the dead empty-token source it first painted.
   */
  hasPlaybackToken: (segment: VideoSegment) => boolean;
  /**
   * Fetch a whole segment with the auth header and return an in-memory object
   * URL for it (the caller owns it and must revokeObjectURL it). Used for audio
   * playback: chunked WebM has no seek cues, so it is only scrubbable when the
   * full file is buffered in memory rather than streamed via a `<source>` URL.
   */
  fetchSegmentObjectUrl: (segment: VideoSegment) => Promise<string | null>;
  /** Download a segment via the authenticated /api/videos endpoint. */
  download: (segment: VideoSegment) => Promise<void>;
  /** segment_index currently downloading, or null. */
  downloading: number | null;
  /** Most recent download error, or null. */
  error: string | null;
}

/**
 * Segment playback data/URL layer. See the module docs for the JSONB-read
 * caveat.
 */
export function useSegmentPlayback({
  sessionId,
  responseId,
  segments,
}: UseSegmentPlaybackArgs): UseSegmentPlaybackResult {
  const { session: authSession } = useSupabase();
  const [downloading, setDownloading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Response-scoped suffix shared by the player, thumbnail, and download URLs
  // so all three resolve the same recording. See the prop docs in the viewer.
  const responseQuery = responseId
    ? `&interview_data_id=${encodeURIComponent(responseId)}`
    : '';

  // processedSegments is sorted by start_ms ascending so the viewer's time
  // math can assume index 0 is the earliest recording. A response can contain
  // both a camera and a screen segment with the same segment_index; keeping
  // the JSONB array order would let whichever was inserted first anchor the
  // transcript clock and hide turns said before the screen share started.
  const processedSegments = useMemo(
    () =>
      (segments?.filter((s) => s.status === 'processed') || [])
        .slice()
        .sort((a, b) => a.start_ms - b.start_ms),
    [segments],
  );

  // In-flight = NOT terminal. Inverting the predicate (vs a status whitelist)
  // future-proofs against intermediate/stuck statuses and keeps `failed` out of
  // this bucket so it is never spun on forever.
  const processingSegments = useMemo(
    () => segments?.filter((s) => !TERMINAL_STATUSES.has(s.status)) || [],
    [segments],
  );

  const failedSegments = useMemo(
    () => segments?.filter((s) => s.status === 'failed') || [],
    [segments],
  );

  const hasProcessedVideo = processedSegments.length > 0;
  const hasFailedSegments = failedSegments.length > 0;
  const allFailed =
    hasFailedSegments &&
    processedSegments.length === 0 &&
    processingSegments.length === 0;

  const accessToken = authSession?.access_token || '';

  // Short-lived signed playback tokens keyed `<kind>:<index>`. The media and
  // thumbnail endpoints accept these instead of the raw Supabase JWT, so the
  // long-lived credential never enters a URL or access log (audit A21). Minted
  // by the authenticated /playback-token endpoint (JWT in the auth header).
  const [playbackTokens, setPlaybackTokens] = useState<Record<string, string>>({});

  // Track the latest fetch so a stale response (older session/response scope)
  // cannot overwrite a newer token set.
  const fetchSeq = useRef(0);

  useEffect(() => {
    if (!accessToken || !hasProcessedVideo) {
      setPlaybackTokens({});
      return undefined;
    }

    let cancelled = false;
    const seq = ++fetchSeq.current;

    const fetchTokens = async () => {
      try {
        const params = new URLSearchParams();
        if (responseId) params.set('interview_data_id', responseId);
        const qs = params.toString();
        const url = `/api/videos/${sessionId}/playback-token${qs ? `?${qs}` : ''}`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to mint playback tokens');
        const body = (await response.json()) as { tokens?: Record<string, string> };
        if (!cancelled && seq === fetchSeq.current) {
          setPlaybackTokens(body.tokens ?? {});
        }
      } catch (err) {
        if (!cancelled) {
          captureException(
            err instanceof Error ? err : String(err),
            'VideoTranscriptViewer.mintPlaybackTokens',
            { sessionId, responseId },
          );
        }
      }
    };

    void fetchTokens();
    const interval = setInterval(() => void fetchTokens(), TOKEN_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [accessToken, hasProcessedVideo, responseId, sessionId]);

  // Tokens mint asynchronously, so on the first render the token is missing and
  // these URLs carry an empty `token=`. That URL 401s, but the URL is keyed by
  // its token: when the real token lands the URL changes, so an <img> that
  // failed the empty-token attempt re-mounts against a fresh URL and recovers
  // (see the keyed onError fallback in VideoTranscriptViewer). The raw Supabase
  // JWT is never placed in these URLs (A21).
  const segmentSrc = useCallback(
    (segment: VideoSegment) => {
      const kind = segment.segment_kind || 'camera';
      const token = playbackTokens[`${kind}:${segment.segment_index}`] || '';
      return `/api/videos/${sessionId}/${segment.segment_index}?token=${encodeURIComponent(
        token,
      )}${responseQuery}&kind=${kind}`;
    },
    [sessionId, playbackTokens, responseQuery],
  );

  const thumbnailSrc = useCallback(
    (segment: VideoSegment) => {
      const kind = segment.segment_kind || 'camera';
      const token = playbackTokens[`${kind}:${segment.segment_index}`] || '';
      return `/api/videos/${sessionId}/thumbnail/${segment.segment_index}?token=${encodeURIComponent(
        token,
      )}${responseQuery}&kind=${kind}`;
    },
    [sessionId, playbackTokens, responseQuery],
  );

  const hasPlaybackToken = useCallback(
    (segment: VideoSegment) => {
      const kind = segment.segment_kind || 'camera';
      return Boolean(playbackTokens[`${kind}:${segment.segment_index}`]);
    },
    [playbackTokens],
  );

  // Fetch a whole segment file with the auth header and wrap it in an in-memory
  // object URL. Audio segments are chunked WebM with no finalized EBML header
  // (no duration, no seek cues), so a streamed <source> URL is not seekable. With
  // the entire file available in memory the browser builds its own seek index and
  // computes the duration, so the native scrubber works without a server remux.
  // The JWT travels in the Authorization header, never in the URL (A21). The
  // caller owns the returned URL and must revokeObjectURL it.
  const fetchSegmentObjectUrl = useCallback(
    async (segment: VideoSegment): Promise<string | null> => {
      if (!authSession?.access_token) return null;

      const kind = segment.segment_kind || 'camera';
      const params = new URLSearchParams();
      if (responseId) params.set('interview_data_id', responseId);
      params.set('kind', kind);
      const url = `/api/videos/${sessionId}/${segment.segment_index}?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      });
      if (!response.ok) throw new Error('Failed to load segment for playback');

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    [authSession?.access_token, responseId, sessionId],
  );

  const download = useCallback(
    async (segment: VideoSegment) => {
      if (!authSession?.access_token) return;

      const segmentIndex = segment.segment_index;
      const kind = segment.segment_kind || 'camera';
      setDownloading(segmentIndex);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (responseId) params.set('interview_data_id', responseId);
        params.set('kind', kind);
        const downloadUrl = `/api/videos/${sessionId}/${segmentIndex}?${params.toString()}`;
        const response = await fetch(downloadUrl, {
          headers: { Authorization: `Bearer ${authSession.access_token}` },
        });

        if (!response.ok) throw new Error('Failed to download video');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildSegmentDownloadFilename(sessionId, segment);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        captureException(
          err instanceof Error ? err : String(err),
          'VideoTranscriptViewer.downloadVideo',
          { sessionId, segmentIndex, kind },
        );
        setError(err instanceof Error ? err.message : 'Download failed');
      } finally {
        setDownloading(null);
      }
    },
    [authSession?.access_token, responseId, sessionId],
  );

  return {
    processedSegments,
    processingSegments,
    failedSegments,
    hasProcessedVideo,
    hasFailedSegments,
    allFailed,
    segmentSrc,
    thumbnailSrc,
    hasPlaybackToken,
    fetchSegmentObjectUrl,
    download,
    downloading,
    error,
  };
}
